(() => {
  const namespace = window.AuremeStaffstartDefaultOverrides;
  const runtime = namespace?.runtime;
  const coordinateIndex = namespace?.coordinateIndex;
  if (!runtime || !coordinateIndex) return;

  const {
    createEmptyMarkedElements,
    findAncestorUntilRoot,
    getTrimmedText,
    isCoordinateIndexPath,
    patchOrder,
    registerPatch,
    syncAttributeMarkers,
  } = runtime;
  const { attributes, constants, selectors } = coordinateIndex;

  /**
   * テキストを持つ子要素があるかどうかを返す。
   * @param {HTMLElement} element - 判定対象要素
   * @returns {boolean}
   */
  function hasTextChildElement(element) {
    return Array.from(element.children).some((child) => {
      return child instanceof HTMLElement && Boolean(getTrimmedText(child));
    });
  }

  /**
   * STAFF SNAP 一覧カードとして扱える要素かどうかを返す。
   * @param {HTMLElement} element - 判定対象要素
   * @returns {boolean}
   */
  function isCoordinateIndexCardElement(element) {
    const coordinateLinks = element.querySelectorAll(selectors.coordinateDetailLink);
    const staffLinks = element.querySelectorAll(selectors.staffDetailLink);

    return coordinateLinks.length === 1 && staffLinks.length === 1;
  }

  /**
   * STAFF SNAP 一覧カード内のスタッフ詳細リンクかどうかを返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {HTMLAnchorElement} link - スタッフ詳細リンク
   * @returns {boolean}
   */
  function isCoordinateIndexCardStaffLink(root, link) {
    return Boolean(
      findAncestorUntilRoot(
        link.parentElement,
        root,
        isCoordinateIndexCardElement,
        constants.coordinateCardParentSearchLimit,
      ),
    );
  }

  /**
   * スタッフ詳細リンク内のプロフィール画像 alt からスタッフ名を返す。
   * @param {HTMLAnchorElement} link - スタッフ詳細リンク
   * @returns {string}
   */
  function getCoordinateIndexStaffNameFromImageAlt(link) {
    const image = link.querySelector('img[alt]');
    if (!(image instanceof HTMLImageElement)) return '';

    return image.alt.trim();
  }

  /**
   * STAFF SNAP 一覧カード内のスタッフ名テキストとして扱えるかどうかを返す。
   * @param {string} text - 判定対象テキスト
   * @param {string} expectedName - プロフィール画像 alt から得たスタッフ名
   * @returns {boolean}
   */
  function isCoordinateIndexStaffNameText(text, expectedName) {
    if (!text) return false;
    // カード内には身長表記（例: 165cm）も同じ階層のテキストとして並ぶため、身長パターンを除外して名前候補に絞る
    if (constants.staffHeightPattern.test(text)) return false;

    // alt が空でスタッフ名を特定できない場合は、身長表記を除外できた時点で名前候補として採用する
    return !expectedName || text === expectedName;
  }

  /**
   * STAFF SNAP 一覧カード内のスタッフ名要素を返す。
   * @param {HTMLAnchorElement} link - スタッフ詳細リンク
   * @returns {HTMLElement | null}
   */
  function findCoordinateIndexStaffNameElement(link) {
    const expectedName = getCoordinateIndexStaffNameFromImageAlt(link);

    for (const candidate of link.querySelectorAll(selectors.staffNameText)) {
      if (!(candidate instanceof HTMLElement)) continue;
      if (hasTextChildElement(candidate)) continue;

      const text = getTrimmedText(candidate);
      if (!isCoordinateIndexStaffNameText(text, expectedName)) continue;

      return candidate;
    }

    const linkText = getTrimmedText(link);
    if (!hasTextChildElement(link) && isCoordinateIndexStaffNameText(linkText, expectedName)) {
      return link;
    }

    return null;
  }

  /**
   * STAFF SNAP 一覧カード内のスタッフ名要素を一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectCoordinateIndexStaffNames(root) {
    const staffNames = new Set();

    for (const link of root.querySelectorAll(selectors.staffDetailLink)) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (!isCoordinateIndexCardStaffLink(root, link)) continue;

      const staffName = findCoordinateIndexStaffNameElement(link);
      if (staffName) staffNames.add(staffName);
    }

    return staffNames;
  }

  /**
   * STAFF SNAP 一覧カード内のスタッフ名 marker を同期する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {Set<HTMLElement>} elements - marker 対象
   */
  function syncCoordinateIndexStaffNameMarkers(root, elements) {
    syncAttributeMarkers(root, attributes.staffName, elements);
  }

  registerPatch({
    order: patchOrder.coordinateIndex.staffName,
    isTargetRoute: isCoordinateIndexPath,
    collect: collectCoordinateIndexStaffNames,
    createEmptyTargets: createEmptyMarkedElements,
    apply: syncCoordinateIndexStaffNameMarkers,
  });
})();
